/**
 * @description Customer portal estimate detail view with accept/decline actions
 *
 * Key features:
 * - Full estimate display: merchant/customer info, line items, totals
 * - Customer actions: Accept, Decline, Accept & Pay (atomic convert + redirect)
 * - Status-dependent UI: action buttons for sent, badges for terminal states
 * - Expiration awareness: shows warning for expired estimates
 * - No payment data (estimates don't track payments)
 * - Download PDF button (always visible, triggers browser download)
 *
 * @dependencies
 * - portalService: getEstimate, acceptEstimate, declineEstimate, acceptAndConvertEstimate
 * - StatusBadge: estimate_status type for status display
 * - amountUtils: formatCurrency
 *
 * @notes
 * - Mirrors PortalInvoiceDetails structure but without payment/refund sections
 * - Accept & Pay: atomic backend call (accept → convert → return invoice) then redirect to payment
 * - Customer accesses estimates only via email links (no browse/list page)
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import portalService from '../../services/portalService';
import { formatCurrency } from '../../utils/amountUtils';
import { formatDate as formatDateSafe, parseDateSafely } from '../../utils/dateUtils';
import StatusBadge from '../shared/StatusBadge';

function PortalEstimateDetails() {
  const { estimateNumber } = useParams();
  const context = useOutletContext();
  const publicId = context?.customerPublicId || context?.customerInfo?.customer?.publicId || context?.portalData?.customerPublicId;
  const navigate = useNavigate();
  const [estimate, setEstimate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null); // 'accept' | 'decline' | 'convert' | null
  const [actionError, setActionError] = useState(null);
  const [declineReason, setDeclineReason] = useState('');
  const [showDeclineForm, setShowDeclineForm] = useState(false);
  const [showAcceptConfirm, setShowAcceptConfirm] = useState(false);
  const [showConvertConfirm, setShowConvertConfirm] = useState(false);

  const fetchEstimateDetails = useCallback(async () => {
    if (!estimateNumber) return;
    if (!publicId) return;

    try {
      setLoading(true);
      const response = await portalService.getEstimate(publicId, estimateNumber);
      setEstimate(response);
    } catch (err) {
      console.error('Error fetching estimate:', err);
      setError('Failed to load estimate details');
    } finally {
      setLoading(false);
    }
  }, [publicId, estimateNumber]);

  useEffect(() => {
    fetchEstimateDetails();
  }, [fetchEstimateDetails]);

  // Thin wrapper around shared timezone-safe formatter — preserves N/A fallback
  // for missing expiration_date (estimates without expirations).
  const formatDate = (dateString) => dateString ? formatDateSafe(dateString) : 'N/A';

  const handleAccept = async () => {
    try {
      setActionError(null);
      setActionLoading('accept');
      setShowAcceptConfirm(false);
      await portalService.acceptEstimate(publicId, estimateNumber);
      await fetchEstimateDetails();
    } catch (err) {
      console.error('Error accepting estimate:', err);
      setActionError(err.response?.data?.error || 'Failed to accept estimate');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDecline = async () => {
    try {
      setActionError(null);
      setActionLoading('decline');
      await portalService.declineEstimate(publicId, estimateNumber, declineReason || null);
      setShowDeclineForm(false);
      setDeclineReason('');
      await fetchEstimateDetails();
    } catch (err) {
      console.error('Error declining estimate:', err);
      setActionError(err.response?.data?.error || 'Failed to decline estimate');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDownloadPdf = async () => {
    try {
      setActionError(null);
      setActionLoading('download');
      const blob = await portalService.downloadEstimatePdf(publicId, estimateNumber);
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `${estimateNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading estimate PDF:', err);
      setActionError(err.response?.data?.error || 'Failed to download estimate PDF');
    } finally {
      setActionLoading(null);
    }
  };

  const handleAcceptAndConvert = async () => {
    try {
      setActionError(null);
      setActionLoading('convert');
      setShowConvertConfirm(false);
      const result = await portalService.acceptAndConvertEstimate(publicId, estimateNumber);

      if (!result?.invoice?.id) {
        setActionError('Estimate was accepted but invoice creation failed. Please contact the merchant.');
        setActionLoading(null);
        return;
      }

      // Navigate to payment page with the new invoice
      navigate(`../pay`, {
        state: {
          selectedInvoices: [result.invoice.id],
          publicId: publicId,
          customerInfo: context?.customerInfo,
          portalData: context?.portalData,
          fromUserAction: true
        }
      });
    } catch (err) {
      console.error('Error converting estimate:', err);
      setActionError(err.response?.data?.error || 'Failed to accept and convert estimate');
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand"></div>
      </div>
    );
  }

  if (error || !estimate) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 mb-4">{error || 'Estimate not found'}</p>
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-gray-600 hover:text-gray-800 mx-auto"
        >
          Go Back
        </button>
      </div>
    );
  }

  // TZ-safe expiration check: parse expirationDate as local-midnight so an estimate
  // expiring May 6 doesn't flip to "expired" on May 5 evening in negative-offset zones.
  const expDate = parseDateSafely(estimate.expirationDate);
  const todayStart = new Date(); todayStart.setHours(0, 0, 0, 0);
  const isExpired = estimate.isExpired || (estimate.status === 'sent' && expDate && expDate < todayStart);
  const canTakeAction = estimate.status === 'sent' && !isExpired;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Inline action error banner */}
      {actionError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start justify-between">
          <p className="text-sm text-red-700">{actionError}</p>
          <button
            onClick={() => setActionError(null)}
            className="ml-4 text-red-400 hover:text-red-600 flex-shrink-0"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}
      {/* Header */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-gray-600 hover:text-gray-800"
        >
          <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </button>
        <StatusBadge
          type="estimate_status"
          value={isExpired ? 'expired' : estimate.status}
          size="sm"
        />
      </div>

      {/* Expired Warning Banner */}
      {isExpired && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-orange-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <p className="text-sm text-orange-700 font-medium">
              This estimate expired on {formatDate(estimate.expirationDate)}. Please contact the merchant for an updated estimate.
            </p>
          </div>
        </div>
      )}

      {/* Estimate Details Card */}
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        {/* Estimate Header */}
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Estimate {estimate.estimateNumber}</h1>
              <p className="text-sm text-gray-600 mt-1">
                Issued on {formatDate(estimate.estimateDate || estimate.issueDate)}
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-600">Total</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(estimate.totalAmount)}
              </p>
            </div>
          </div>
        </div>

        {/* Estimate Body */}
        <div className="p-6 space-y-6">
          {/* Merchant and Customer Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* From */}
            <div>
              <h3 className="text-sm font-medium text-gray-600 mb-2">From</h3>
              <p className="font-semibold text-gray-900">{estimate.merchantName}</p>
              {estimate.merchantDisplayAddress && (
                <p className="text-sm text-gray-600">{estimate.merchantDisplayAddress}</p>
              )}
              {(estimate.merchantDisplayCity || estimate.merchantDisplayState || estimate.merchantDisplayPostalCode) && (
                <p className="text-sm text-gray-600">
                  {[estimate.merchantDisplayCity, estimate.merchantDisplayState, estimate.merchantDisplayPostalCode]
                    .filter(Boolean)
                    .join(', ')}
                </p>
              )}
              {estimate.merchantDisplayEmail && (
                <p className="text-sm text-gray-600">{estimate.merchantDisplayEmail}</p>
              )}
              {estimate.merchantDisplayPhone && (
                <p className="text-sm text-gray-600">{estimate.merchantDisplayPhone}</p>
              )}
              {estimate.merchantDisplayWebsite && (
                <p className="text-sm text-gray-600">{estimate.merchantDisplayWebsite}</p>
              )}
            </div>

            {/* To */}
            <div>
              <h3 className="text-sm font-medium text-gray-600 mb-2">To</h3>
              <p className="font-semibold text-gray-900">
                {estimate.customerCompany || `${estimate.customerFirstName} ${estimate.customerLastName}`}
              </p>
              {estimate.customerEmail && (
                <p className="text-sm text-gray-600">{estimate.customerEmail}</p>
              )}
            </div>
          </div>

          {/* Estimate Details */}
          <div className="border-t border-gray-200 pt-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-gray-600">Estimate Date</p>
                <p className="font-medium">{formatDate(estimate.estimateDate || estimate.issueDate)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Expiration Date</p>
                <p className={`font-medium ${isExpired ? 'text-orange-600' : ''}`}>
                  {estimate.expirationDate ? formatDate(estimate.expirationDate) : 'No expiration'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Payment Terms</p>
                <p className="font-medium">
                  {estimate.paymentTerms?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </p>
              </div>
              {estimate.poNumber && (
                <div>
                  <p className="text-sm text-gray-600">PO Number</p>
                  <p className="font-medium">{estimate.poNumber}</p>
                </div>
              )}
            </div>
          </div>

          {/* Customer Notes */}
          {estimate.customerNotes && (
            <div className="border-t border-gray-200 pt-6">
              <h3 className="text-sm font-medium text-gray-600 mb-2">Notes</h3>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{estimate.customerNotes}</p>
            </div>
          )}

          {/* Line Items */}
          <div className="border-t border-gray-200 pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Estimate Items</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left text-sm font-medium text-gray-600 pb-2">Description</th>
                    <th className="text-right text-sm font-medium text-gray-600 pb-2">Qty</th>
                    <th className="text-right text-sm font-medium text-gray-600 pb-2">Rate</th>
                    <th className="text-right text-sm font-medium text-gray-600 pb-2">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {estimate.items?.map((item, index) => (
                    <tr key={index} className="border-b border-gray-100">
                      <td className="py-3">
                        <p className="font-medium text-gray-900">{item.itemName}</p>
                        {item.description && (
                          <p className="text-sm text-gray-600">{item.description}</p>
                        )}
                      </td>
                      <td className="text-right py-3 text-gray-700">{item.quantity}</td>
                      <td className="text-right py-3 text-gray-700">{formatCurrency(item.unitPrice)}</td>
                      <td className="text-right py-3 font-medium text-gray-900">{formatCurrency(item.totalPrice)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Totals */}
          <div className="border-t border-gray-200 pt-6">
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Subtotal</span>
                <span className="font-medium">{formatCurrency(estimate.subtotal)}</span>
              </div>
              {(estimate.appliedDiscounts || []).length > 0
                ? estimate.appliedDiscounts.map((row, index) => {
                  const amount = Number(row.amountCents ?? row.amount_cents ?? 0) / 100;
                  if (amount <= 0) return null;
                  return (
                    <div key={`named-discount-${index}`} className="flex justify-between text-sm">
                      <span className="text-gray-600">{row.title || 'Discount'}</span>
                      <span className="font-medium text-green-600">-{formatCurrency(amount)}</span>
                    </div>
                  );
                })
                : ((estimate.discountAmount || 0) > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Discount</span>
                    <span className="font-medium text-green-600">-{formatCurrency(estimate.discountAmount)}</span>
                  </div>
                ))}
              {estimate.taxAmount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Tax ({estimate.taxRate}%)</span>
                  <span className="font-medium">{formatCurrency(estimate.taxAmount)}</span>
                </div>
              )}
              {estimate.shippingCost > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Shipping</span>
                  <span className="font-medium">{formatCurrency(estimate.shippingCost)}</span>
                </div>
              )}
              <div className="flex justify-between text-lg font-semibold pt-2 border-t">
                <span>Total</span>
                <span>{formatCurrency(estimate.totalAmount)}</span>
              </div>
            </div>
          </div>

          {/* Terms & Footer */}
          {(estimate.terms || estimate.footer) && (
            <div className="border-t border-gray-200 pt-6 space-y-4">
              {estimate.terms && (
                <div>
                  <h3 className="text-sm font-medium text-gray-600 mb-2">Terms & Conditions</h3>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{estimate.terms}</p>
                </div>
              )}
              {estimate.footer && (
                <div>
                  <p className="text-sm text-gray-600 whitespace-pre-wrap">{estimate.footer}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
          {/* Decline form (expandable) */}
          {showDeclineForm && (
            <div className="mb-4 p-4 bg-white rounded-lg border border-gray-200">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason for declining (optional)
              </label>
              <textarea
                value={declineReason}
                onChange={(e) => setDeclineReason(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
                rows={3}
                maxLength={500}
                placeholder="Let the merchant know why you're declining..."
              />
              <div className="flex gap-2 mt-3">
                <button
                  onClick={handleDecline}
                  disabled={actionLoading === 'decline'}
                  className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  {actionLoading === 'decline' ? 'Declining...' : 'Confirm Decline'}
                </button>
                <button
                  onClick={() => { setShowDeclineForm(false); setDeclineReason(''); }}
                  className="px-4 py-2 bg-white text-gray-700 text-sm font-medium border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Accept confirmation (expandable) */}
          {showAcceptConfirm && (
            <div className="mb-4 p-4 bg-white rounded-lg border border-green-200">
              <p className="text-sm text-gray-700 mb-3">
                Are you sure you want to accept this estimate for <strong>{formatCurrency(estimate.totalAmount)}</strong>?
              </p>
              <div className="flex gap-2">
                <button
                  onClick={handleAccept}
                  disabled={actionLoading === 'accept'}
                  className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                >
                  {actionLoading === 'accept' ? 'Accepting...' : 'Confirm Accept'}
                </button>
                <button
                  onClick={() => setShowAcceptConfirm(false)}
                  className="px-4 py-2 bg-white text-gray-700 text-sm font-medium border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Accept & Pay confirmation (expandable) */}
          {showConvertConfirm && (
            <div className="mb-4 p-4 bg-white rounded-lg border border-blue-200">
              <p className="text-sm text-gray-700 mb-3">
                This will accept the estimate and create an invoice for <strong>{formatCurrency(estimate.totalAmount)}</strong>. You'll be redirected to the payment page.
              </p>
              <div className="flex gap-2">
                <button
                  onClick={handleAcceptAndConvert}
                  disabled={actionLoading === 'convert'}
                  className="px-4 py-2 bg-brand text-white text-sm font-medium rounded-lg hover:bg-brand-hover transition-colors disabled:opacity-50"
                >
                  {actionLoading === 'convert' ? 'Processing...' : 'Confirm Accept & Pay'}
                </button>
                <button
                  onClick={() => setShowConvertConfirm(false)}
                  className="px-4 py-2 bg-white text-gray-700 text-sm font-medium border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3 justify-between">
            {/* Left side actions */}
            <div className="flex gap-3">
              <button
                onClick={handleDownloadPdf}
                disabled={actionLoading === 'download'}
                className="px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium disabled:opacity-50"
              >
                {actionLoading === 'download' ? 'Downloading...' : 'Download PDF'}
              </button>
              {canTakeAction && !showDeclineForm && !showAcceptConfirm && !showConvertConfirm && (
                <button
                  onClick={() => setShowDeclineForm(true)}
                  disabled={!!actionLoading}
                  className="px-4 py-2 bg-white text-red-600 border border-red-300 rounded-lg hover:bg-red-50 transition-colors font-medium disabled:opacity-50"
                >
                  Decline
                </button>
              )}
            </div>

            {/* Right side actions */}
            <div className="flex gap-3">
              {canTakeAction && !showDeclineForm && !showAcceptConfirm && !showConvertConfirm && (
                <>
                  <button
                    onClick={() => setShowAcceptConfirm(true)}
                    disabled={!!actionLoading}
                    className="px-6 py-2 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                  >
                    Accept Estimate
                  </button>
                  <button
                    onClick={() => setShowConvertConfirm(true)}
                    disabled={!!actionLoading}
                    className="px-6 py-2 bg-brand text-white font-medium rounded-lg hover:bg-brand-hover transition-colors disabled:opacity-50"
                  >
                    Accept & Pay
                  </button>
                </>
              )}

              {/* Status badges for terminal states */}
              {estimate.status === 'accepted' && (
                <span className="px-4 py-2 bg-green-100 text-green-800 font-medium rounded-lg inline-flex items-center">
                  <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  Accepted
                </span>
              )}
              {estimate.status === 'declined' && (
                <span className="px-4 py-2 bg-red-100 text-red-800 font-medium rounded-lg">
                  Declined
                </span>
              )}
              {estimate.status === 'converted' && estimate.convertedInvoiceNumber && (
                <button
                  onClick={() => navigate(`../invoices/${estimate.convertedInvoiceNumber}`)}
                  className="px-6 py-2 bg-brand text-white font-medium rounded-lg hover:bg-brand-hover transition-colors"
                >
                  View Invoice &rarr;
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PortalEstimateDetails;
